import cn from 'classnames';
import { useCombobox, useMultipleSelection } from 'downshift';
import * as React from 'react';

const { useState, useMemo } = React;

export interface InviteMembersProps {
  members?: {
    email: string;
  }[];
  emptyUsers?: boolean;
  inviteBtnText?: string;
  onUpdateEmail: (selectedItems: any, inputValue: any) => void;
  className?: string;
  label?: string;
  defaultValue?: any;
}

export const InviteMembers = ({
  members = [],
  onUpdateEmail,
  className,
  emptyUsers,
  label,
  defaultValue,
}: InviteMembersProps) => {
  const [inputValue, setInputValue] = useState('');

  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection<{
    email: string;
  }>({ initialSelectedItems: defaultValue });

  React.useEffect(() => {
    onUpdateEmail(selectedItems, inputValue);
  }, [selectedItems, inputValue]);

  React.useEffect(() => {
    emptyUsers && selectedItems.splice(0, selectedItems.length);
    setInputValue('');
  }, [emptyUsers]);

  const filteredItems = useMemo(
    () =>
      members.filter((member) => member.email.toLowerCase().startsWith(inputValue.toLowerCase())),
    [members, inputValue, selectedItems]
  );

  const isValidNewEmail = useMemo(() => {
    return (
      filteredItems.length === 0 &&
      validateEmail(inputValue) &&
      !selectedItems.find((obj) => obj.email === inputValue)
    );
  }, [filteredItems, inputValue, selectedItems]);

  const {
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
  } = useCombobox({
    inputValue,
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    selectedItem: null,
    items: filteredItems,

    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          inputValue !== undefined && setInputValue(inputValue);
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (selectedItem) {
            setInputValue('');
            addSelectedItem(selectedItem);
          }
          break;
        default:
          break;
      }
    },
  });

  const handleAddNewEmail = () => {
    const tempInput = inputValue.trim();
    if (tempInput.length !== 0) {
      if (tempInput[0] === ',' && tempInput.length > 1) {
        addSelectedItem({
          email: tempInput.slice(1),
        });
      } else if (tempInput[0] !== ',') {
        {
          addSelectedItem({
            email: tempInput,
          });
        }
      }
    }

    setInputValue('');
  };

  return (
    <div className="flex flex-col justify-between h-full">
      <div className="h-full">
        <div>
          <div
            {...getComboboxProps()}
            className={cn({
              'border-b border-gray-200 flex w-full items-center': true,
              [className || '']: !!className,
            })}
          >
            <div className="flex items-center flex-wrap w-full py-6">
              <div className="mr-8 text-sm">{label}</div>
              {selectedItems.map(
                (selectedItem, index) =>
                  selectedItem?.email && (
                    <div
                      key={`selected-item-${index}`}
                      {...getSelectedItemProps({ selectedItem, index })}
                      className={cn({
                        'mt-4 flex items-center  px-6 py-3 text-xs mr-4': true,
                        'bg-primary-100 text-primary-500': validateEmail(selectedItem.email),
                        'bg-red-100 text-red-500': !validateEmail(selectedItem.email),
                      })}
                    >
                      {selectedItem.email}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeSelectedItem(selectedItem);
                        }}
                        className="ml-5"
                      >
                        &#10005;
                      </button>
                    </div>
                  )
              )}
              <input
                {...getInputProps(
                  getDropdownProps({
                    onKeyDown: (e) => {
                      if (e.code === 'Enter' || e.code === 'Comma' || e.code === 'Space') {
                        handleAddNewEmail();
                      }
                    },
                  })
                )}
                className="bg-transparent flex-grow"
              />
            </div>
          </div>
        </div>

        {/* {isValidNewEmail ? (
          <button
            className="flex w-full items-center py-16 px-20 bg-gray-100"
            onClick={handleAddNewEmail}>
            <span>{inputValue}</span>
          </button>
        ) : null} */}

        <ul {...getMenuProps()}>
          {filteredItems.map((item, index) => (
            <li
              key={`${item}${index}`}
              {...getItemProps({ item, index })}
              className={cn('list-none flex py-16 px-20 cursor-pointer', {
                'bg-gray-100': highlightedIndex === index,
              })}
            >
              <div className="ml-12">
                {/* <p className="text-sm font-medium">{item.firstname}</p> */}
                <p className="text-xs">
                  <span className="">{item.email}</span>
                </p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

function validateEmail(emailAddress: string) {
  //eslint-disable-next-line
  var sQtext = '[^\\x0d\\x22\\x5c\\x80-\\xff]';
  const sDtext = '[^\\x0d\\x5b-\\x5d\\x80-\\xff]';
  const sAtom =
    '[^\\x00-\\x20\\x22\\x28\\x29\\x2c\\x2e\\x3a-\\x3c\\x3e\\x40\\x5b-\\x5d\\x7f-\\xff]+';
  const sQuotedPair = '\\x5c[\\x00-\\x7f]';
  const sDomainLiteral = '\\x5b(' + sDtext + '|' + sQuotedPair + ')*\\x5d';
  const sQuotedString = '\\x22(' + sQtext + '|' + sQuotedPair + ')*\\x22';
  const sDomain_ref = sAtom;
  const sSubDomain = '(' + sDomain_ref + '|' + sDomainLiteral + ')';
  const sWord = '(' + sAtom + '|' + sQuotedString + ')';
  const sDomain = sSubDomain + '(\\x2e' + sSubDomain + ')*';
  const sLocalPart = sWord + '(\\x2e' + sWord + ')*';
  const sAddrSpec = sLocalPart + '\\x40' + sDomain; // complete RFC822 email address spec
  const sValidEmail = '^' + sAddrSpec + '$'; // as whole string

  const reValidEmail = new RegExp(sValidEmail);

  return reValidEmail.test(emailAddress);
}
